import { fetchAuthSession } from 'aws-amplify/auth';
import { Plant, BedPlan } from '../types';
import radishImg from '../data/radish.svg';
import beetrootImg from '../data/beetroot.svg';
import cabbageImg from '../data/cabbage.svg';

const BASE = process.env.REACT_APP_API_URL;

// The signed Cognito token proving who the user is. The server reads the user's
// identity from this — the client never sends a userId itself anymore.
async function authHeader(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return token ? { Authorization: token } : {};
}

// Presentation-only data lives here, NOT in DynamoDB (see Phase 2 decision).
// Glyphs: emoji by default; a few plants use a bundled SVG (image overrides emoji).
const PLANT_GLYPHS: Record<string, { emoji: string; image?: string }> = {
  tomato: { emoji: '🍅' },
  carrot: { emoji: '🥕' },
  lettuce: { emoji: '🥬' },
  basil: { emoji: '🌿' },
  fennel: { emoji: '🌾' },
  potato: { emoji: '🥔' },
  courgette: { emoji: '🥒' },
  onion: { emoji: '🧅' },
  peas: { emoji: '🫛' },
  garlic: { emoji: '🧄' },
  radish: { emoji: '🌸', image: radishImg },
  spinach: { emoji: '🍃' },
  cucumber: { emoji: '🥒' },
  pepper: { emoji: '🫑' },
  cabbage: { emoji: '🥬', image: cabbageImg },
  broccoli: { emoji: '🥦' },
  strawberry: { emoji: '🍓' },
  beetroot: { emoji: '🟣', image: beetrootImg },
  beans: { emoji: '🫘' },
};

// Family colour is derived from `family`, not stored in the DB.
const FAMILY_COLOURS: Record<string, string> = {
  Solanaceae: '#e07a5f',
  Apiaceae: '#f4a261',
  Asteraceae: '#52b788',
  Lamiaceae: '#40916c',
  Cucurbitaceae: '#74c69d',
  Allium: '#c77dff',
  Fabaceae: '#95d5b2',
  Brassicaceae: '#e63946',
  Amaranthaceae: '#2d6a4f',
  Rosaceae: '#ff70a6',
};

export const fetchPlants = async (): Promise<Plant[]> => {
  const res = await fetch(`${BASE}/plants`);
  if (!res.ok) throw new Error(`Failed to load plants (${res.status})`);
  const plants = (await res.json()) as Plant[];
  // Re-attach presentation data the API doesn't carry.
  return plants.map(p => ({
    ...p,
    ...PLANT_GLYPHS[p.plantId],
    familyColour: FAMILY_COLOURS[p.family],
  }));
};

export const fetchGardens = async (): Promise<BedPlan[]> => {
  const res = await fetch(`${BASE}/gardens`, { headers: await authHeader() });
  if (!res.ok) throw new Error(`Failed to load gardens (${res.status})`);
  return (await res.json()) as BedPlan[];
};

export const saveGarden = async (plan: BedPlan): Promise<void> => {
  const res = await fetch(`${BASE}/gardens`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(plan),
  });
  if (!res.ok) throw new Error(`Failed to save garden (${res.status})`);
};

export const deleteGarden = async (gardenId: string): Promise<void> => {
  const params = new URLSearchParams({ gardenId });
  const res = await fetch(`${BASE}/gardens?${params}`, {
    method: 'DELETE',
    headers: await authHeader(),
  });
  if (!res.ok) throw new Error(`Failed to delete garden (${res.status})`);
};
